/**
 * End Customer IPAM API Service
 *
 * Service para end customers gerenciarem ranges DHCP/estatico,
 * alocacoes de IP e integrar com gateway DHCP.
 */

import type { IPSubnet } from '../types/network'
import { apiClient } from './api'

export type ConfigureSubnetRangesRequest = {
  dhcp_range_start?: string | null
  dhcp_range_end?: string | null
  static_range_start?: string | null
  static_range_end?: string | null
  dns_servers?: string[] | null
}

export type SubnetAvailability = {
  subnet_id: string
  dhcp_range: {
    start: string
    end: string
    total: number
    available: number
  } | null
  static_range: {
    start: string
    end: string
    total: number
    available: number
    available_ips: string[]
  } | null
  unranged: {
    total: number
    available: number
  }
}

export type AllocationEntry = {
  id: string
  ip_address: string
  mac_address?: string | null
  hostname?: string | null
  status: string
  resource_type?: string | null
  vm_instance_id?: string | null
  allocated_at?: string | null
}

export type DHCPLease = {
  ip_address: string
  mac_address?: string | null
  hostname?: string | null
}

export const endCustomerIpamApi = {
  // Subnets
  async listSubnets(): Promise<{ total: number; subnets: IPSubnet[] }> {
    return apiClient.get('/api/v1/end-customer/ipam/subnets')
  },

  async getSubnet(subnetId: string): Promise<IPSubnet> {
    return apiClient.get(`/api/v1/end-customer/ipam/subnets/${subnetId}`)
  },

  // Range configuration
  async configureRanges(subnetId: string, data: ConfigureSubnetRangesRequest): Promise<IPSubnet> {
    return apiClient.put(`/api/v1/end-customer/ipam/subnets/${subnetId}/ranges`, data)
  },

  // Availability
  async getAvailability(subnetId: string): Promise<SubnetAvailability> {
    return apiClient.get(`/api/v1/end-customer/ipam/subnets/${subnetId}/availability`)
  },

  // Allocations
  async listAllocations(
    subnetId: string,
  ): Promise<{ total: number; subnet_id: string; allocations: AllocationEntry[] }> {
    return apiClient.get(`/api/v1/end-customer/ipam/subnets/${subnetId}/allocations`)
  },

  async allocateStaticIP(params: {
    subnet_id: string
    ip_address?: string
    hostname?: string
    vm_instance_id?: string
  }): Promise<AllocationEntry> {
    return apiClient.post('/api/v1/end-customer/ipam/allocate-static', null, { params })
  },

  async releaseAllocation(allocationId: string): Promise<void> {
    await apiClient.delete(`/api/v1/end-customer/ipam/allocations/${allocationId}`)
  },

  // DHCP
  async applyDHCP(subnetId: string): Promise<{ success: boolean; gateway_id: string }> {
    return apiClient.post(`/api/v1/end-customer/ipam/subnets/${subnetId}/dhcp/apply`)
  },

  async getDHCPLeases(subnetId: string): Promise<{ total: number; leases: DHCPLease[] }> {
    return apiClient.get(`/api/v1/end-customer/ipam/subnets/${subnetId}/dhcp/leases`)
  },
}
